import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { classNames } from 'primereact/utils';
import { CompetitionService } from '../../service/CompetitionService';
import { translatedMessage } from '../../service/LanguageService';

let emptySubject = {
    id: null,
    name: '',
    score: '',
    competition: {
        id: null
    },
    bibliography: [],
    proposals: []
};

const SubjectEditDialog = (props) => {
    const [subject, setSubject] = useState(emptySubject);
    const [isNew, setIsNew] = useState(false);
    const [dialogVisible, setDialogVisible] = useState(false);

    const toast = useRef(null);

    const competitionService = useMemo(() => new CompetitionService(), []);

    useEffect(() => {
        let _subject = props.value ? props.value : emptySubject;
        _subject.bibliography = _subject.bibliography.map((sb) => (sb.competitionBibliography ? sb.competitionBibliography : sb));
        setSubject(_subject);

        setIsNew(!props.value?.id);
    }, [props]);

    useEffect(() => {
        setDialogVisible(props.visible);
    }, [props.visible]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: subject,
        validate: (data) => {
            let errors = {};

            if (!data.name) {
                errors.name = translatedMessage('form.error.name.required');
            }
            if (!data.score) {
                errors.score = translatedMessage('form.error.score.required');
            }
            return errors;
        },
        onSubmit: (data) => {
            saveSubject(data);
            formik.resetForm();
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const hideSubjectDialog = () => {
        if (typeof props.visibleSetter === 'function') {
            props.visibleSetter(false);
        }
        setDialogVisible(false);
        formik.resetForm();
        refreshParent();
    };

    const refreshParent = () => {
        if (typeof props.afterSave === 'function') {
            props.afterSave(subject, false);
        }
    };

    const saveSubject = (data) => {
        data.competition = { id: props.competition.id };
        competitionService
            .saveSubject(data)
            .then((savedSubject) => {
                if (typeof props.afterSave === 'function') {
                    props.afterSave(savedSubject, isNew);
                }
                toast.current.show({ severity: 'success', summary: translatedMessage('generic.save.success') });
                hideSubjectDialog();
            })
            .catch((error) => {
                toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 });
            });
    };

    const subjectDialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideSubjectDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-save" className="p-button-text" form="subject-form" type="submit" />
        </>
    );
    const setSelectedRows = (value) => {
        formik.setFieldValue('bibliography', value);
    };

    return (
        <>
            <Toast ref={toast} />
            <Dialog 
                visible={dialogVisible} 
                // style={{ width: '850px' }} 
                header={translatedMessage('subject.details')} 
                modal 
                className="pcn-dialog p-fluid" 
                footer={subjectDialogFooter} 
                onHide={hideSubjectDialog}
            >
                <form id="subject-form" onSubmit={formik.handleSubmit}>
                    <div className="p-inputgroup">
                        <span className="p-float-label">
                            <InputText type="text" id="name" name="name" value={formik.values.name} onChange={formik.handleChange} autoFocus className={classNames({ 'p-invalid': isFormFieldValid('name') })} />
                            <label htmlFor="name" className={classNames({ 'p-error': isFormFieldValid('name') })}>
                                {translatedMessage('generic.name')} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('name')}

                    <div className="p-inputgroup mt-3">
                        <span className="p-float-label">
                            <InputNumber
                                id="score"
                                name="score"
                                value={formik.values.score}
                                onValueChange={formik.handleChange}
                                min={0}
                                max={100}
                                className={classNames({ 'p-invalid': isFormFieldValid('score') })}
                                useGrouping={false}
                                maxFractionDigits={2}
                            />
                            <label htmlFor="score" className={classNames({ 'p-error': isFormFieldValid('score') })}>
                                {translatedMessage('subject.score')} *
                            </label>
                        </span>
                    </div>
                    {getFormErrorMessage('score')}

                    <DataTable
                        value={props.competitionBibliography}
                        selectionMode={true ? null : 'checkbox'}
                        selection={formik.values.bibliography}
                        onSelectionChange={(e) => setSelectedRows(e.value)}
                        selectionPageOnly={true}
                        dataKey="id"
                        responsiveLayout="scroll"
                        className='mt-3'
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: '1%', minWidth: '3rem' }}></Column>
                        <Column field="bibliography" header={translatedMessage('subject.bibliography')} headerStyle={{ width: '40%', minWidth: '10rem' }}/>
                        <Column field="topic" header={translatedMessage('subject.bibliography.topic')} headerStyle={{ width: '50%', minWidth: '10rem' }}/>
                    </DataTable>
                </form>
            </Dialog>
        </>
    );
};

export default SubjectEditDialog;
